/**
 * @file NarrativeViewComponent.tsx
 *
 * @description A React component that provides a read-only view of narrative text
 * with an embedded easter egg that must be found and deleted before proceeding.
 * This component is used in the final review stage to ensure officers carefully read
 * the narratives before submission.
 *
 * @module Components
 * @requires Material-UI
 * @requires React
 */

import React, { useMemo, useState, useEffect, useRef } from "react";
import { Box, useTheme, alpha } from "@mui/material";
import { EASTER_EGGS } from "../../shared/constants";
import { useNarrativeEasterEggEnabled } from "../../hooks/useNarrativeEasterEggEnabled";
import { WidgetProps } from "@rjsf/utils";

/**
 * @name NarrativeViewComponent
 *
 * @description Component for viewing narrative text with embedded easter egg
 *
 * This component:
 * - Displays narrative text in a pseudo-editable div
 * - Embeds a random easter egg within the text
 * - Prevents editing of regular text
 * - Allows deletion of the easter egg text only
 * - Notifies parent when easter egg is successfully removed
 *
 * @param {NarrativeViewWidgetProps} props - Component properties
 *
 * @returns {JSX.Element} The rendered narrative view component
 */
export const NarrativeViewWidget: React.FC<WidgetProps> = (props) => {
  const theme = useTheme();
  const { value, registry, id } = props;

  // Extract props from formContext if not provided directly
  const easterEggFoundRef = registry?.formContext?.narrativeEasterEggRefs;
  const onEasterEggFound = registry?.formContext?.onEasterEggFound;

  const { enabled: narrativeEasterEggEnabled, loaded: easterEggFlagLoaded } =
    useNarrativeEasterEggEnabled();
  // Only treat the gate as disabled once the flag has actually loaded; otherwise
  // async hydration would briefly look "disabled" and permanently bypass it.
  const easterEggDisabled = easterEggFlagLoaded && !narrativeEasterEggEnabled;

  // Ref for the easter egg span to scroll and highlight
  const easterEggSpanRef = useRef<HTMLSpanElement>(null);
  const [isHighlighted, setIsHighlighted] = useState(false);

  // Ensure the per-narrative ref entry exists during render so synchronous
  // reads (state init, render-time fallbacks, key handlers) never see undefined.
  if (easterEggFoundRef && !easterEggFoundRef.current[id]) {
    easterEggFoundRef.current[id] = React.createRef<boolean>();
  }
  const narrativeEasterEggRef = easterEggFoundRef?.current[id];

  // Track easter egg state locally to trigger re-renders
  const [easterEggFound, setEasterEggFound] = useState(
    narrativeEasterEggRef?.current === true || easterEggDisabled
  );

  // Track whether parent has already been notified to enforce a single
  // false -> true transition notification.
  const notifiedParentRef = useRef(false);

  useEffect(() => {
    if (!narrativeEasterEggRef) return;
    if (!easterEggFlagLoaded) return;

    // Resolve completion from prior ref completion OR feature flag disabled.
    const completed =
      narrativeEasterEggRef.current === true || easterEggDisabled;

    // Only ever flip the ref to true; never overwrite an existing true with false.
    if (completed) {
      narrativeEasterEggRef.current = true;
      setEasterEggFound(true);
    }
  }, [narrativeEasterEggRef, easterEggDisabled, easterEggFlagLoaded]);

  // Notify parent once when the resolved completion transitions to true.
  useEffect(() => {
    if (easterEggFound && !notifiedParentRef.current) {
      notifiedParentRef.current = true;
      onEasterEggFound?.();
    }
  }, [easterEggFound, onEasterEggFound]);

  // Calculate a random index to insert the easter egg within the narrative text
  const splitNarrativeTextIndex = useMemo(() => {
    return Math.floor(Math.random() * (value?.split(" ").length ?? 0));
  }, [value]);

  // Select a random easter egg from the predefined list (only if easter egg not found and text exists)
  const randomEasterEgg = useMemo(() => {
    if (!easterEggFound && value && value.trim() !== "") {
      return EASTER_EGGS[Math.floor(Math.random() * EASTER_EGGS.length)];
    }
    return "";
  }, [easterEggFound, value]);

  // Split narrative text to place easter egg in between (only if easter egg not found and text exists)
  const firstHalfNarrative = useMemo(() => {
    if (!easterEggFound && value && value.trim() !== "") {
      return (
        value?.split(" ").slice(0, splitNarrativeTextIndex).join(" ") ?? ""
      );
    }
    return "";
  }, [easterEggFound, value, splitNarrativeTextIndex]);

  const secondHalfNarrative = useMemo(() => {
    if (!easterEggFound && value && value.trim() !== "") {
      return value?.split(" ").slice(splitNarrativeTextIndex).join(" ") ?? "";
    }
    return "";
  }, [easterEggFound, value, splitNarrativeTextIndex]);

  /**
   * @name handleEasterEgg
   *
   * @description Handles easter egg discovery when user deletes the easter egg text
   *
   * @param {React.KeyboardEvent} e - Keyboard event
   */
  const handleEasterEgg = (e: React.KeyboardEvent<HTMLSpanElement>) => {
    e.preventDefault();
    if (e.key === "Backspace" || e.key === "Delete") {
      if (narrativeEasterEggRef) {
        narrativeEasterEggRef.current = true;
      }
      setEasterEggFound(true);
    }
  };

  /**
   * @name handleEditingDisabledText
   *
   * @description Prevents editing of non-easter egg text
   *
   * @param {React.KeyboardEvent | React.FormEvent} e - User input event
   */
  const handleEditingDisabledText = (
    e: React.SyntheticEvent<HTMLSpanElement>
  ) => {
    e.preventDefault();
  };

  /**
   * @name handleHighlightEasterEgg
   *
   * @description Scrolls to and highlights the easter egg
   */
  const handleHighlightEasterEgg = () => {
    if (easterEggSpanRef.current) {
      easterEggSpanRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      setIsHighlighted(true);
      // Remove highlight after 3 seconds
      setTimeout(() => setIsHighlighted(false), 3000);
    }
  };

  // For newly added narratives (empty text), mark easter egg as found automatically
  if (!value || value.trim() === "") {
    if (narrativeEasterEggRef && !narrativeEasterEggRef.current) {
      narrativeEasterEggRef.current = true;
      setEasterEggFound(true);
    }
  }

  // If easter egg is found, display narrative as plain read-only text
  if (easterEggFound) {
    return (
      <Box
        sx={{
          border: 1,
          borderColor: "action.disabled",
          borderRadius: 1,
          p: 2,
          minHeight: "40px",
          backgroundColor: "action.hover",
          mb: 1,
          textAlign: "left",
          whiteSpace: "pre-wrap",
          color: "text.secondary",
        }}
      >
        {value || "No value provided"}
      </Box>
    );
  }

  // Easter egg not found - display with easter egg embedded
  return (
    <>
      <div
        style={{
          border: `2px solid ${theme.palette.warning.main}`,
          borderRadius: "4px",
          padding: "8px",
          minHeight: "40px",
          backgroundColor: theme.palette.mode === "dark" 
            ? alpha(theme.palette.warning.main, 0.1) 
            : "#fff3e0",
          boxShadow: "inset 0 1px 3px rgba(0, 0, 0, 0.1)",
          cursor: "text",
          marginBottom: "4px",
          textAlign: "left",
          userSelect: "none",
          WebkitUserSelect: "none",
        }}
      >
        <span
          onMouseDown={(e) => e.preventDefault()}
          contentEditable
          suppressContentEditableWarning
          style={{
            outline: "none",
            textAlign: "left",
            whiteSpace: "pre-wrap",
            userSelect: "none",
            WebkitUserSelect: "none",
          }}
          onInput={handleEditingDisabledText}
          onKeyDown={handleEditingDisabledText}
        >
          {" "}
          {firstHalfNarrative}{" "}
        </span>
        <span
          ref={easterEggSpanRef}
          contentEditable
          suppressContentEditableWarning
          onKeyDown={handleEasterEgg}
          style={{
            outline: "none",
            userSelect: "none",
            WebkitUserSelect: "none",
            backgroundColor: isHighlighted ? "#ff5722" : undefined,
            color: isHighlighted ? "#fff" : undefined,
            padding: isHighlighted ? "2px 4px" : undefined,
            borderRadius: isHighlighted ? "4px" : undefined,
            transition: "all 0.3s ease-in-out",
            animation: isHighlighted ? "pulse 0.5s ease-in-out 3" : undefined,
          }}
        >
          {" "}
          {randomEasterEgg}{" "}
        </span>
        <span
          onMouseDown={(e) => e.preventDefault()}
          contentEditable
          suppressContentEditableWarning
          style={{
            outline: "none",
            textAlign: "left",
            whiteSpace: "pre-wrap",
            userSelect: "none",
            WebkitUserSelect: "none",
          }}
          onInput={handleEditingDisabledText}
          onKeyDown={handleEditingDisabledText}
        >
          {" "}
          {secondHalfNarrative}{" "}
        </span>
      </div>
      <div
        style={{
          backgroundColor: theme.palette.mode === "dark" 
            ? alpha(theme.palette.warning.main, 0.1) 
            : "#fff3e0",
          border: `1px solid ${theme.palette.warning.main}`,
          borderRadius: "4px",
          padding: "12px",
          marginBottom: "8px",
          color: theme.palette.text.primary,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "12px",
          }}
        >
          <div>
            <div
              style={{
                color: theme.palette.mode === "dark" 
                  ? theme.palette.warning.main 
                  : "#e65100",
                fontSize: "0.875rem",
                fontWeight: 600,
                marginBottom: "4px",
              }}
            >
              ⚠️ Easter Egg Found - Action Required
            </div>
            <div
              style={{
                color: theme.palette.mode === "dark" 
                  ? alpha(theme.palette.text.primary, 0.9) 
                  : "#bf360c",
                fontSize: "0.8rem",
                lineHeight: 1.5,
              }}
            >
              To verify you've read the narrative, please find the highlighted
              nonsensical phrase (easter egg) hidden in the text above and delete it
              by clicking on it and pressing
              <strong> Backspace</strong> or <strong>Delete</strong>.
            </div>
          </div>
          {narrativeEasterEggEnabled && (
            <button
              onClick={handleHighlightEasterEgg}
              style={{
                backgroundColor: "#ff5722",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                padding: "8px 16px",
                fontSize: "0.8rem",
                fontWeight: 600,
                cursor: "pointer",
                whiteSpace: "nowrap",
                flexShrink: 0,
                boxShadow: "0 2px 4px rgba(255, 87, 34, 0.3)",
                transition: "all 0.2s ease-in-out",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#e64a19";
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 4px 8px rgba(255, 87, 34, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#ff5722";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 4px rgba(255, 87, 34, 0.3)";
              }}
            >
              🔍 Highlight Easter Egg
            </button>
          )}
        </div>
      </div>
    </>
  );
};
